import { ArrowLeft, ArrowRight, Info, Plus, Settings, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChannelFx } from '@/components/ChannelFx';
import { gradientTextStyle, useCommunityTheme } from '@/components/theme/CommunityThemeScope';
import { BannerCoverRender } from '@/components/theme/BannerLayerRender';
import type { Community } from '@/types';
import { type Channel, channelIcon, fmtCount, groupChannels } from './channels';

interface Props {
  community: Community;
  channels: Channel[];
  activeChannelId: string;
  inChat: boolean;
  isOwner: boolean;
  joined: boolean;
  online: number;
  members: number;
  onBack: () => void;
  onSelectChannel: (id: string) => void;
  onToggleJoin: () => void;
  onOpenOverview: () => void;
  onOpenChannelEditor: (channel?: Channel) => void;
}

/** Left rail: community name/stats, channel groups, and owner channel controls. */
export function ChannelRail({
  community,
  channels,
  activeChannelId,
  inChat,
  isOwner,
  joined,
  online,
  members,
  onBack,
  onSelectChannel,
  onToggleJoin,
  onOpenOverview,
  onOpenChannelEditor,
}: Props) {
  const groups = groupChannels(channels);
  // Live-published Studio styling (e.g. a gradient community title) from the theme scope.
  const theme = useCommunityTheme();
  const gradientTitle =
    theme?.nameStyle === 'gradient'
      ? gradientTextStyle(theme.accent || community.color || '#54b9fe')
      : undefined;

  return (
    <aside className="flex min-h-0 flex-col border-r border-white/[0.05]">
      {/* Pulled up 64px (-mt-16) so the banner fills the top strip behind the app
          header; the extra top padding keeps the name/stats below that strip. */}
      <div className="relative -mt-16 h-36 overflow-hidden bg-ink-950">
        {/* Community banner fills the header; a dark gradient keeps text legible.
            A layered Studio design renders live (real glass); else the hosted image. */}
        {theme?.bannerDesign ? (
          <BannerCoverRender design={theme.bannerDesign} focal={theme.bannerFocal} />
        ) : community.banner ? (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${community.banner})` }}
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{ background: `linear-gradient(150deg, ${community.color}55, rgba(255,255,255,0.025))` }}
          />
        )}
        {/* Back arrow sits on top of the banner image, in the top strip. */}
        <button
          className="absolute left-3 top-[76px] z-10 grid h-9 w-9 place-items-center text-white/85 drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)] transition-all hover:text-white"
          onClick={onBack}
          title="Back"
          aria-label="Back"
        >
          <ArrowLeft size={17} />
        </button>
        <button
          className="absolute right-3 top-[76px] z-10 grid h-8 w-8 place-items-center text-white/70 drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)] transition-all hover:text-accent"
          onClick={onOpenOverview}
          title="Overview"
          aria-label="Overview"
        >
          <Info size={15} strokeWidth={2.2} />
        </button>
      </div>

      <div className="border-b border-white/[0.06] px-4 pb-5 pt-3">

        <div className="relative flex items-center gap-2">
          <div className="min-w-0 flex-1 truncate text-[15px] font-extrabold text-[#fafafa]" style={gradientTitle}>
            {community.name}
          </div>
          {isOwner && (
            <span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-accent/15 text-accent" title="Admin access">
              <ShieldCheck size={14} strokeWidth={2.4} />
            </span>
          )}
        </div>
        <div className="relative mt-1 flex items-center gap-1.5 text-[11px] text-white/[0.46] [&_b]:font-bold [&_b]:text-[#fafafa]">
          <span className="h-[7px] w-[7px] animate-cv2-breath rounded-full bg-[#22c55e] shadow-[0_0_8px_rgba(34,197,94,0.7)]" />
          <b>{fmtCount(online)}</b> online
          <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
          <b>{fmtCount(members)}</b> members
        </div>

        {!joined && community.price_cents === 0 && (
          <button
            className="relative mt-3 flex h-9 w-full items-center justify-center gap-2 rounded-[10px] border-0 bg-[#fafafa] px-3.5 text-[13px] font-bold tracking-[-0.1px] text-[#0a0a0e] shadow-[0_6px_22px_rgba(0,0,0,0.35)] transition-all duration-150 hover:brightness-[1.05]"
            onClick={onToggleJoin}
          >
            <>
              Join community <ArrowRight size={15} strokeWidth={2.5} />
            </>
          </button>
        )}
      </div>

      <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto px-2 pb-4 pt-2">
        {groups.map((group) => (
          <div key={group.section}>
            <div className="flex items-center justify-between px-2.5 pb-1.5 pt-3.5 text-[11px] font-bold uppercase tracking-[0.7px] text-white/[0.46]">
              <span>{group.section}</span>
              {isOwner && (
                <button
                  className="grid h-[18px] w-[18px] place-items-center rounded-[5px] border-0 bg-transparent text-white/[0.46] transition-all duration-150 hover:bg-white/[0.07] hover:text-[#fafafa]"
                  title="Add channel"
                  onClick={() => onOpenChannelEditor()}
                >
                  <Plus size={13} strokeWidth={2.5} />
                </button>
              )}
            </div>
            {group.items.map((ch) => {
              const Icon = channelIcon(ch);
              const active = inChat && ch.id === activeChannelId;
              return (
                <div
                  key={ch.id}
                  className={cn(
                    'group relative my-0.5 flex cursor-pointer items-center gap-2 rounded-sm border border-transparent px-2.5 py-2 text-sm font-medium text-white/[0.88] transition-[background,color,border-color] duration-150 hover:bg-white/[0.04] hover:text-[#fafafa]',
                    active &&
                      "border-[#54b9fe4d] bg-[#54b9fe24] text-[#fafafa] before:absolute before:-left-2 before:bottom-2 before:top-2 before:w-[3px] before:rounded-[0_3px_3px_0] before:bg-accent before:shadow-[0_0_10px_#54b9fe99] before:content-['']",
                  )}
                  onClick={() => onSelectChannel(ch.id)}
                >
                  <ChannelFx effect={ch.effect} />
                  <span
                    className={cn(
                      'inline-flex h-5 w-5 flex-none items-center justify-center',
                      ch.kind === 'voice' ? 'text-[#4ade80]' : active ? 'text-accent' : 'text-white/[0.46]',
                    )}
                  >
                    <Icon size={18} strokeWidth={2} />
                  </span>
                  <span className="min-w-0 flex-1 truncate">{ch.name}</span>
                  {isOwner && (
                    <button
                      type="button"
                      className="grid h-7 w-7 flex-none place-items-center rounded-[8px] border border-transparent text-white/[0.4] opacity-0 transition-all hover:border-white/[0.08] hover:bg-white/[0.06] hover:text-white group-hover:opacity-100"
                      title={`Edit ${ch.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenChannelEditor(ch);
                      }}
                    >
                      <Settings size={14} strokeWidth={2} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </aside>
  );
}
